import { useState } from 'react';
import { Globe, Copy, Eye, EyeOff, MoreVertical, Trash2, RefreshCw, Edit, BarChart3, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useToast } from '../ui/toast';
import { copyToClipboard, formatNumber } from '../../lib/utils';
import type { Site } from '../../types';

interface SiteCardProps {
  site: Site;
  onEdit: (site: Site) => void;
  onDelete: (site: Site) => void;
  onRegenerateKeys: (site: Site) => void;
  onViewStats: (site: Site) => void;
}

export function SiteCard({ site, onEdit, onDelete, onRegenerateKeys, onViewStats }: SiteCardProps) {
  const [showSecret, setShowSecret] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { addToast } = useToast();

  const handleCopy = async (value: string, field: string) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      addToast({
        title: 'Скопійовано!',
        description: `${field} скопійовано в буфер обміну`,
        type: 'success',
      });
    }
  };

  const maskSecret = (secret: string) => {
    return secret.substring(0, 8) + '•'.repeat(24) + secret.substring(secret.length - 4);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Globe className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">{site.name}</h3>
            <p className="text-sm text-muted-foreground">{site.domain}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={site.is_active ? 'success' : 'secondary'}>
            {site.is_active ? 'Активний' : 'Неактивний'}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(site)}>
                <Edit className="mr-2 h-4 w-4" />
                Редагувати
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onViewStats(site)}>
                <BarChart3 className="mr-2 h-4 w-4" />
                Статистика
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRegenerateKeys(site)}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Оновити ключі
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDelete(site)} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Видалити
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* API Key */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">API Key</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-muted px-3 py-2 rounded-md font-mono truncate">
              {site.api_key}
            </code>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleCopy(site.api_key, 'API Key')}
            >
              {copiedField === 'API Key' ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Secret Key */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Secret Key</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-muted px-3 py-2 rounded-md font-mono truncate">
              {showSecret ? site.secret_key : maskSecret(site.secret_key)}
            </code>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSecret(!showSecret)}
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleCopy(site.secret_key, 'Secret Key')}
            >
              {copiedField === 'Secret Key' ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="text-sm">
            <span className="text-muted-foreground">Верифікацій: </span>
            <span className="font-semibold">{formatNumber(site.verification_count)}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => onViewStats(site)}>
            <BarChart3 className="mr-2 h-4 w-4" />
            Статистика
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
